import JSON5 from 'json5'

export const ECHARTS_OPTION_TYPES = ['line', 'bar', 'pie', 'radar', 'scatter', 'funnel', 'gauge', 'pictorialbar', 'wordcloud']
const roots = ['title', 'legend', 'tooltip', 'grid', 'xAxis', 'yAxis', 'polar', 'radiusAxis', 'angleAxis', 'radar', 'visualMap', 'dataZoom', 'color', 'backgroundColor', 'textStyle', 'animation', 'animationDuration', 'animationDurationUpdate', 'animationEasing', 'animationEasingUpdate', 'series', 'aria']
const forbidden = /formatter|script|token|password|deviceId|deviceName|productId|productName|previewProduct|pageId|targetPage|authorization|__proto__|constructor|prototype/i
const resourceKeys = ['url', 'href', 'link', 'image', 'src', 'extraCssText', 'dataQuery', 'parameter', 'navigation', 'markerConfig', 'infoWindowConfig']
const object = value => value !== null && typeof value === 'object' && !Array.isArray(value)
export const supportsEchartsOption = kind => ECHARTS_OPTION_TYPES.includes(kind)

// 只解析声明式对象，不使用 eval/Function；与现有组件资源的纯 JSON 校验保持一致。
export function validateEchartsOption (option, kind) {
  if (!supportsEchartsOption(kind)) throw new Error('此组件暂不支持 Option 编辑')
  if (!object(option)) throw new Error('Option 必须是一个对象，例如 { xAxis: {}, series: [] }')
  let count = 0
  const visit = (value, depth = 0) => {
    if (depth > 20 || ++count > 20000) throw new Error('Option 配置过大或嵌套过深')
    if (Array.isArray(value)) {
      if (value.length > 10000) throw new Error('Option 单个数组不能超过 10000 项')
      return value.map(item => visit(item, depth + 1))
    }
    if (object(value)) {
      if (![Object.prototype, null].includes(Object.getPrototypeOf(value))) throw new Error('Option 只能包含普通对象')
      const result = {}
      Object.keys(value).forEach(key => {
        if (forbidden.test(key) || resourceKeys.includes(key)) throw new Error('暂不支持字段：' + key + '（不开放函数、脚本或外部资源）')
        result[key] = visit(value[key], depth + 1)
      })
      return result
    }
    if (typeof value === 'number' && !Number.isFinite(value)) throw new Error('Option 数值必须是有限数字')
    if (value !== null && !['string', 'number', 'boolean'].includes(typeof value)) throw new Error('Option 不支持函数或表达式')
    if (typeof value === 'string' && (value.length > 4096 || /<\s*(script|iframe|object|embed)|<\/?\s*[a-z!][^>]*>|on[a-z]+\s*=|javascript\s*:|data\s*:|image:\/\/|https?:\/\/|^\/\/|\burl\s*\(/i.test(value))) throw new Error('Option 不支持可执行内容、外部图片或过长文本')
    return value
  }
  const result = visit(option)
  Object.keys(result).forEach(key => { if (!roots.includes(key)) throw new Error('暂不支持 Option 顶层字段：' + key) })
  if (JSON.stringify(result).length > 100000) throw new Error('Option 不能超过 100 KB')
  if (result.series !== undefined) {
    if (!Array.isArray(result.series) || result.series.length > 50) throw new Error('series 必须是数组，且不能超过 50 个系列')
    const kinds = ['line', 'bar'].includes(kind) ? ['line', 'bar'] : [kind === 'pictorialbar' ? 'pictorialBar' : kind === 'wordcloud' ? 'wordCloud' : kind]
    result.series.forEach(series => {
      if (!object(series) || (series.type !== undefined && !kinds.includes(series.type))) throw new Error('系列类型须与当前图表一致，可用类型：' + kinds.join('、'))
    })
  }
  return result
}

export function parseEchartsOption (text, kind) {
  if (typeof text !== 'string' || text.length > 100000) throw new Error('Option 不能超过 100 KB')
  const source = text.trim().replace(/^option\s*=\s*/, '').replace(/;\s*$/, '')
  let value
  try { value = JSON5.parse(source) } catch (error) {
    throw new Error('配置格式有误' + (error.lineNumber ? '（第 ' + error.lineNumber + ' 行，第 ' + error.columnNumber + ' 列）' : '') + '。仅支持对象，不支持函数、变量或表达式。')
  }
  return validateEchartsOption(value, kind)
}

function clone (value) {
  if (Array.isArray(value)) return value.map(clone)
  if (object(value)) return Object.keys(value).reduce((out, key) => { out[key] = clone(value[key]); return out }, {})
  return value // 原图表生成的受信函数保持兼容，粘贴内容已在上面禁止函数。
}
function overlay (base, patch) {
  if (!object(patch)) return clone(patch)
  const result = object(base) ? clone(base) : {}
  Object.keys(patch).forEach(key => { result[key] = object(patch[key]) ? overlay(result[key], patch[key]) : clone(patch[key]) })
  return result
}

// 每次从原图表本轮生成的 Option 合并，不修改源对象，也不清空或重建图表实例。
export function composeEchartsOption (generated, custom, dynamic = false, preserveSeriesStyle = false) {
  const result = overlay(generated, custom)
  const baseSeries = generated.series || []
  if (custom.series) {
    result.series = (dynamic ? baseSeries : custom.series).map((entry, index) => {
      const base = baseSeries[index] || baseSeries[0] || {}
      const style = custom.series[index] || custom.series[0] || {}
      // 科技仪表仅显式选择此合并方式；经典图表保持原有系列接管规则。
      const series = preserveSeriesStyle ? overlay(base, style) : { type: base.type, ...clone(style) }
      if (dynamic || style.data === undefined) series.data = clone(base.data || [])
      if ((dynamic || style.name === undefined) && base.name !== undefined) series.name = base.name
      return series
    })
    if (!dynamic && result.legend) {
      const legends = Array.isArray(result.legend) ? result.legend : [result.legend]
      const styles = Array.isArray(custom.legend) ? custom.legend : [custom.legend || {}]
      legends.forEach((legend, i) => { if ((styles[i] || {}).data === undefined) delete legend.data })
    }
  } else if (custom.color) {
    // 原库的自动配色回调优先于调色盘，显式设置 color 时移除这层默认回调。
    const strip = value => {
      if (!value || typeof value !== 'object') return
      if (value.itemStyle) {
        if (typeof value.itemStyle.color === 'function') delete value.itemStyle.color
        if (value.itemStyle.normal && typeof value.itemStyle.normal.color === 'function') delete value.itemStyle.normal.color
      }
      Object.values(value).forEach(item => { if (item && typeof item === 'object') strip(item) })
    }
    result.series.forEach(strip)
  }
  if (dynamic) {
    // 当前 categories/series 数据协议不改变坐标系；新推送覆盖示例数据，保留轴线等样式。
    for (const key of ['xAxis', 'yAxis', 'radar', 'legend']) {
      if (!generated[key]) continue
      const bases = Array.isArray(generated[key]) ? generated[key] : [generated[key]]
      const styles = Array.isArray(result[key]) ? result[key] : [result[key] || {}]
      const merged = bases.map((base, i) => {
        const value = overlay(base, styles[i] || styles[0])
        for (const field of key === 'radar' ? ['indicator'] : key === 'legend' ? ['data'] : ['type', 'data']) {
          if (base[field] !== undefined) value[field] = clone(base[field])
        }
        return value
      })
      result[key] = Array.isArray(generated[key]) ? merged : merged[0]
    }
  }
  return result
}
